import { cn } from "@/lib/cn";
import type { PisoFachada } from "@/lib/edificio";

export interface FachadaProps {
  etiqueta: string;
  /** De arriba hacia abajo (`pisosFachada` de `lib/edificio.ts`). */
  pisos: PisoFachada[];
  elegido: number | null;
  onElegir: (piso: number) => void;
}

/** Color suave del estado para rellenar (el token entero queda para el borde). */
export function rellenoEstado(token: string, porcentaje = 30): string {
  return `color-mix(in srgb, var(${token}) ${porcentaje}%, transparent)`;
}

/**
 * El frente del edificio: un botón por piso, con una ventana por unidad pintada por su estado (ancho según sus m²) y
 * cuántas quedan disponibles. Tocar un piso llama a `onElegir`; los pisos sin unidades no se tocan. Un piso con anexos
 * (el dúplex con su azotea) es un solo botón más alto, con las ventanas de punta a punta: se resaltan y eligen juntos.
 */
export function Fachada({ etiqueta, pisos, elegido, onElegir }: FachadaProps) {
  return (
    <div role="group" aria-label={etiqueta} className="mx-auto w-full max-w-md">
      <div aria-hidden className="mx-8 h-px bg-carbon/40" />
      <div className="flex flex-col border-x border-carbon/40 px-1.5 py-1.5">
        {pisos.map((p) => {
          const activo = p.piso === elegido;
          const alto = `${2.5 * p.etiquetas.length}rem`;
          return (
            <button
              key={p.piso}
              type="button"
              disabled={p.total === 0}
              aria-pressed={activo}
              aria-label={`${p.nombre}: ${p.resumen}`}
              onClick={() => onElegir(p.piso)}
              className={cn(
                "relative flex items-center gap-3 rounded-control px-2 text-left text-carbon transition-colors",
                p.total > 0 && "cursor-pointer hover:bg-carbon/5",
                activo && "bg-marca/10 hover:bg-marca/10",
              )}
              style={{ height: alto }}
            >
              {activo && <span aria-hidden className="absolute inset-y-2 -left-1.5 w-0.5 rounded-full bg-marca" />}
              <span
                className={cn("flex h-full w-8 shrink-0 flex-col justify-around text-cuerpo font-light tabular-nums", p.total === 0 && "text-carbon/35", activo && "font-normal text-marca")}
              >
                {p.etiquetas.map((e) => (
                  <span key={e}>{e}</span>
                ))}
              </span>
              <span aria-hidden className="flex min-w-0 flex-1 gap-1" style={{ height: `calc(${alto} - 1rem)` }}>
                {p.ventanas.length === 0 ? (
                  <span className="flex-1 rounded-sm border border-dashed border-carbon/20" />
                ) : (
                  p.ventanas.map((v) => (
                    <span key={v.id} className="min-w-1.5 rounded-[3px]" style={{ flexGrow: v.peso, flexBasis: 0, backgroundColor: rellenoEstado(v.token, 80) }} />
                  ))
                )}
              </span>
              <span
                aria-hidden
                className={cn("w-12 shrink-0 text-right text-[0.625rem] font-medium uppercase tracking-[0.14em]", p.disponibles > 0 ? "text-[var(--color-estado-disponible)]" : "text-carbon/35")}
              >
                {p.total === 0 ? "" : p.disponibles > 0 ? `${p.disponibles} disp.` : "—"}
              </span>
            </button>
          );
        })}
      </div>
      <div aria-hidden className="h-1 rounded-full bg-carbon" />
    </div>
  );
}
